import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { DevPanel } from './components/dev/dev-panel';
import { ModeBadge } from './components/dev/mode-badge';
import { queryClient } from './lib/query-client';
import { initSessionExpiryHandling } from './lib/session-expiry';
import { routeTree } from './routeTree.gen';
import './styles.css';

// ── Sentry (optional) ────────────────────────────────────────────────────────
// Uncomment to enable error reporting. Requires VITE_SENTRY_DSN at build time.
//
// import * as Sentry from '@sentry/react';
// if (import.meta.env.VITE_SENTRY_DSN) {
//   Sentry.init({
//     dsn: import.meta.env.VITE_SENTRY_DSN,
//     environment: import.meta.env.MODE,
//     tracesSampleRate: 0.1,
//   });
// }

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

initSessionExpiryHandling(router);

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

// Start MSW in development before mounting so every fetch is intercepted
// from the first render. The dynamic import is tree-shaken in production.
async function enableMocking(): Promise<void> {
  if (!import.meta.env.DEV) return;
  const { worker } = await import('./mocks/browser');
  // `bypass`: routes we don't mock fall through to the network (Vite proxy → Tahiti).
  await worker.start({ onUnhandledRequest: 'bypass' });
}

void enableMocking().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
      <ModeBadge />
      <DevPanel />
    </StrictMode>,
  );
});
